import { HISTORY_MAX_STACK } from '@src/constants/history';
import { actionTypes } from './actions';

export const initialState = {
  dubbing: { past: [], future: [], latestAudioCheckpoint: null },
};

// Maps `captureSnapshots` field names → entry field names for the head
// past entry and the mirror future[0] entry. The head's "after" state and
// future[0]'s "before" state describe the same point in the timeline, so
// any snapshot refresh must update both sides in lockstep.
const SNAPSHOT_HEAD_FIELDS = {
  projectAudio: 'projectAudioAfter',
  blockPlayState: 'blockPlayStateAfter',
  originalBlocks: 'originalBlocksAfter',
};

const SNAPSHOT_FUTURE_FIELDS = {
  projectAudio: 'projectAudioBefore',
  blockPlayState: 'blockPlayStateBefore',
  originalBlocks: 'originalBlocksBefore',
};

const mergeSnapshotsInto = (entry, snapshots, fieldMap) => {
  if (!entry) return entry;
  const next = { ...entry };
  Object.keys(snapshots).forEach((key) => {
    const value = snapshots[key];
    if (value !== undefined) next[fieldMap[key]] = value;
  });
  return next;
};

// Refresh the head past entry and the mirror future[0] entry with new
// snapshot data. Returns the unchanged slice if there's no head entry.
const refreshHead = (slice, snapshots) => {
  if (!slice || slice.past.length === 0) return slice;
  const lastIdx = slice.past.length - 1;
  return {
    ...slice,
    past: [
      ...slice.past.slice(0, lastIdx),
      mergeSnapshotsInto(slice.past[lastIdx], snapshots, SNAPSHOT_HEAD_FIELDS),
    ],
    future:
      slice.future.length > 0
        ? [
            mergeSnapshotsInto(
              slice.future[0],
              snapshots,
              SNAPSHOT_FUTURE_FIELDS,
            ),
            ...slice.future.slice(1),
          ]
        : slice.future,
  };
};

const historyReducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.HISTORY_COMMIT: {
      const { scope, entry } = action.payload;
      const slice = state[scope] || {
        past: [],
        future: [],
        latestAudioCheckpoint: null,
      };
      const nextPast = [...slice.past, entry];
      const cappedPast =
        nextPast.length > HISTORY_MAX_STACK
          ? nextPast.slice(nextPast.length - HISTORY_MAX_STACK)
          : nextPast;
      return {
        ...state,
        [scope]: {
          past: cappedPast,
          future: [],
          latestAudioCheckpoint: slice.latestAudioCheckpoint,
        },
      };
    }
    case actionTypes.HISTORY_UNDO: {
      const { scope } = action.payload;
      const slice = state[scope];
      if (!slice || slice.past.length === 0) return state;
      const entry = slice.past[slice.past.length - 1];
      return {
        ...state,
        [scope]: {
          past: slice.past.slice(0, -1),
          future: [entry, ...slice.future],
          latestAudioCheckpoint: slice.latestAudioCheckpoint,
        },
      };
    }
    case actionTypes.HISTORY_REDO: {
      const { scope } = action.payload;
      const slice = state[scope];
      if (!slice || slice.future.length === 0) return state;
      const [entry, ...restFuture] = slice.future;
      return {
        ...state,
        [scope]: {
          past: [...slice.past, entry],
          future: restFuture,
          latestAudioCheckpoint: slice.latestAudioCheckpoint,
        },
      };
    }
    case actionTypes.HISTORY_CLEAR: {
      const { scope } = action.payload;
      if (!state[scope]) return state;
      return {
        ...state,
        [scope]: { past: [], future: [], latestAudioCheckpoint: null },
      };
    }
    case actionTypes.HISTORY_SET_AUDIO_CHECKPOINT: {
      const {
        scope,
        audioCheckpoint,
        options: { resetFuture = false } = {},
      } = action.payload;
      const slice = state[scope];
      if (!slice) return state;
      return {
        ...state,
        [scope]: {
          ...slice,
          future: resetFuture ? [] : slice.future,
          latestAudioCheckpoint: audioCheckpoint,
        },
      };
    }
    case actionTypes.HISTORY_REFRESH_HEAD_SNAPSHOTS: {
      const { scope, snapshots } = action.payload;
      const slice = state[scope];
      const nextSlice = refreshHead(slice, snapshots);
      if (nextSlice === slice) return state;
      return { ...state, [scope]: nextSlice };
    }
    case actionTypes.HISTORY_PURGE_REFERENCE: {
      const { scope, slice } = action.payload;
      if (!state[scope] || !slice) return state;
      return {
        ...state,
        [scope]: { ...state[scope], ...slice },
      };
    }
    default:
      return state;
  }
};

export default historyReducer;
